import { useState } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import publicPortalGuide from '../../../../../guides/public-portal.md?raw';
import adminGuide from '../../../../../guides/admin-guide.md?raw';
import technicalAdminGuide from '../../../../../guides/technical-admin.md?raw';

type GuideKey = 'public' | 'admin' | 'technical';

const GUIDES: { key: GuideKey; label: string; content: string }[] = [
  { key: 'public', label: 'Public Site', content: publicPortalGuide },
  { key: 'admin', label: 'Admin & Portal', content: adminGuide },
  { key: 'technical', label: 'Technical Admin', content: technicalAdminGuide },
];

/** Markdown -> Tailwind, matching the rest of the app's phoenix-blue/serif styling — there's no
 * @tailwindcss/typography plugin installed, so every element needs its own class here. */
const MARKDOWN_COMPONENTS = {
  h1: (props: ComponentPropsWithoutRef<'h1'>) => (
    <h1 className="mt-8 font-serif text-3xl font-bold text-phoenix-blue-900 first:mt-0" {...props} />
  ),
  h2: (props: ComponentPropsWithoutRef<'h2'>) => (
    <h2 className="mt-8 border-t border-gray-200 pt-6 font-serif text-xl font-semibold text-phoenix-blue-900 first:mt-0 first:border-0 first:pt-0" {...props} />
  ),
  h3: (props: ComponentPropsWithoutRef<'h3'>) => (
    <h3 className="mt-5 font-serif text-lg font-semibold text-phoenix-blue-800" {...props} />
  ),
  p: (props: ComponentPropsWithoutRef<'p'>) => <p className="mt-3 leading-relaxed text-gray-700" {...props} />,
  ul: (props: ComponentPropsWithoutRef<'ul'>) => (
    <ul className="mt-3 list-disc space-y-1.5 pl-5 text-gray-700" {...props} />
  ),
  ol: (props: ComponentPropsWithoutRef<'ol'>) => (
    <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-gray-700" {...props} />
  ),
  li: (props: ComponentPropsWithoutRef<'li'>) => <li className="leading-relaxed" {...props} />,
  a: (props: ComponentPropsWithoutRef<'a'>) => (
    <a className="text-phoenix-blue-600 underline hover:text-phoenix-blue-700" {...props} />
  ),
  strong: (props: ComponentPropsWithoutRef<'strong'>) => <strong className="font-semibold text-gray-900" {...props} />,
  code: (props: ComponentPropsWithoutRef<'code'>) => (
    <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.85em] text-phoenix-blue-800" {...props} />
  ),
  pre: (props: ComponentPropsWithoutRef<'pre'>) => (
    <pre className="mt-3 overflow-x-auto rounded-md bg-gray-900 p-4 text-xs leading-relaxed text-gray-100" {...props} />
  ),
  hr: () => <hr className="mt-8 border-gray-200" />,
  table: (props: ComponentPropsWithoutRef<'table'>) => (
    <div className="mt-3 overflow-x-auto rounded-md border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm" {...props} />
    </div>
  ),
  thead: (props: ComponentPropsWithoutRef<'thead'>) => <thead className="bg-gray-50" {...props} />,
  th: (props: ComponentPropsWithoutRef<'th'>) => (
    <th className="px-3 py-2 text-left font-semibold text-gray-600" {...props} />
  ),
  tbody: (props: ComponentPropsWithoutRef<'tbody'>) => <tbody className="divide-y divide-gray-100" {...props} />,
  td: (props: ComponentPropsWithoutRef<'td'>) => <td className="px-3 py-2 align-top text-gray-700" {...props} />,
} as const;

export function GuidesPage() {
  const [active, setActive] = useState<GuideKey>('public');
  const guide = GUIDES.find((g) => g.key === active)!;

  return (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <h1 className="font-serif text-3xl font-bold text-phoenix-blue-900">Guides</h1>
      <p className="mt-2 text-sm text-gray-500">
        Reference documentation for the public site, the admin &amp; portal tooling, and the
        technical/deployment internals — kept in sync with the source in <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-phoenix-blue-800">guides/</code>.
      </p>

      <div className="mt-6 flex gap-2 border-b border-gray-200">
        {GUIDES.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => setActive(g.key)}
            className={`-mb-px rounded-t-md border-b-2 px-4 py-2 text-sm font-medium transition ${
              active === g.key
                ? 'border-phoenix-blue-600 text-phoenix-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      <article className="mt-2 pb-8">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
          {guide.content}
        </ReactMarkdown>
      </article>
    </main>
  );
}
